import {
  scaleOrdinal,
  schemeCategory10,
  select,
  max,
  scaleLinear,
  range,
  lineRadial,
  curveCatmullRom,
  selectAll,
  mouse,
} from 'd3'
import 'd3-transition'

export const RadarChart = {
  draw: (nativeElement, data, options) => {
    const cfg = Object.assign(
      {
        w: 600, // Width of the circle
        h: 600, // Height of the circle
        margin: { top: 20, right: 20, bottom: 20, left: 20 }, // The margins of the SVG
        maxValue: 0, // What is the value that the biggest circle will represent
        labelFactor: 1.1, // How much farther than the radius of the outer circle should the labels be placed
        wrapWidth: 60, // The number of pixels after which a label needs to be given a new line
        opacityArea: 0.35, // The opacity of the area of the blob
        dotRadius: 4, // The size of the colored circles of each blog
        opacityCircles: 0.1, // The opacity of the circles of each blob
        strokeWidth: 1, // The width of the stroke around each blob
        roundStrokes: false, // If true the area and stroke will follow a round path (cardinal-closed)
        font: {
          family: 'Vodafone',
          size: '14px',
          weight: 'bold',
        },
        background: {
          color: '#f2f2f4',
          borderColor: '#e4e5e8',
          opacity: 1,
        },
        lines: {
          nbrLevels: 3,
          color: 3,
        },
        popup: {
          element: null,
          template: (i) => i.value,
        },
        area: {
          opacity: 0.1,
          color: scaleOrdinal(schemeCategory10),
          dot: {
            radius: 4,
            color: scaleOrdinal(schemeCategory10),
            opacity: 0.8,
          },
        },
      },
      options,
    )

    let tooltip
    if (cfg.popup.element) {
      tooltip = select(cfg.popup.element)
    }
    // If the supplied maxValue is smaller than the actual one, replace by the max in the data
    const maxValue = Math.max(
      cfg.maxValue,
      max(data, function (i) {
        return max(
          i.map(function (o) {
            return o.value
          }),
        )
      }),
    )

    const allAxis = data[0].map((i, j) => i.category) // Names of each axis
    const total = allAxis.length // The number of different axes
    const radius = Math.min(cfg.w / 2, cfg.h / 2) // Radius of the outermost circle
    const angleSlice = (Math.PI * 2) / total // The width in radians of each 'slice'

    // Scale for the radius
    const rScale = scaleLinear().range([0, radius]).domain([0, maxValue])

    /////////////////////////////////////////////////////////
    //////////// Create the container SVG and g /////////////
    /////////////////////////////////////////////////////////

    // Remove whatever chart with the same id/class was present before
    select(nativeElement).select('svg').remove()

    // Initiate the radar chart SVG
    const svg = select(nativeElement)
      .append('svg')
      .attr('width', cfg.w + cfg.margin.left + cfg.margin.right)
      .attr('height', cfg.h + cfg.margin.top + cfg.margin.bottom)
      .attr('class', 'radar' + nativeElement)
    // Append a g element
    const g = svg
      .append('g')
      .attr(
        'transform',
        'translate(' +
          (cfg.w / 2 + cfg.margin.left) +
          ',' +
          (cfg.h / 2 + cfg.margin.top) +
          ')',
      )

    /////////////////////////////////////////////////////////
    ////////// Glow filter for some extra pizzazz ///////////
    /////////////////////////////////////////////////////////

    // Filter for the outside glow
    const filter = g.append('defs').append('filter').attr('id', 'glow')
    const feMerge = filter.append('feMerge')
    const feMergeNode_1 = feMerge
      .append('feMergeNode')
      .attr('in', 'coloredBlur')
    const feMergeNode_2 = feMerge
      .append('feMergeNode')
      .attr('in', 'SourceGraphic')

    /////////////////////////////////////////////////////////
    /////////////// Draw the Circular grid //////////////////
    /////////////////////////////////////////////////////////

    // Wrapper for the grid & axes
    const axisGrid = g.append('g').attr('class', 'axisWrapper')

    // Draw the background circles
    axisGrid
      .selectAll('.levels')
      .data(range(1, cfg.lines.nbrLevels + 1).reverse())
      .enter()
      .append('circle')
      .attr('class', 'gridCircle')
      .attr('r', (d, i) => (radius / cfg.lines.nbrLevels) * d)
      .style('fill', cfg.background.color)
      .style('stroke', cfg.background.borderColor)
      .style('fill-opacity', cfg.background.opacity)
      .style('filter', 'url(#glow)')

    /////////////////////////////////////////////////////////
    //////////////////// Draw the axes //////////////////////
    /////////////////////////////////////////////////////////

    // Create the straight lines radiating outward from the center
    const axis = axisGrid
      .selectAll('.axis')
      .data(allAxis)
      .enter()
      .append('g')
      .attr('class', 'axis')
    // Append the lines
    axis
      .append('line')
      .attr('x1', 0)
      .attr('y1', 0)
      .attr('x2', function (d, i) {
        return rScale(maxValue * 1.1) * Math.cos(angleSlice * i - Math.PI / 2)
      })
      .attr('y2', function (d, i) {
        return rScale(maxValue * 1.1) * Math.sin(angleSlice * i - Math.PI / 2)
      })
      .attr('class', 'line')
      .style('stroke', 'white')
      .style('stroke-width', '2px')

    // Append the labels at each axis
    axis
      .append('text')
      .attr('class', 'legend')
      .style('font-family', cfg.font.family)
      .style('font-size', cfg.font.size)
      .style('font-weight', cfg.font.weight)
      .attr('text-anchor', 'middle')
      .attr('dy', '0.1em')
      .attr('x', function (d, i) {
        return (
          rScale(maxValue * cfg.labelFactor) *
          Math.cos(angleSlice * i - Math.PI / 2)
        )
      })
      .attr('y', function (d, i) {
        return (
          rScale(maxValue * cfg.labelFactor) *
          Math.sin(angleSlice * i - Math.PI / 2)
        )
      })
      .text(function (d) {
        return d
      })
      .call(wrap, cfg.wrapWidth)

    /////////////////////////////////////////////////////////
    ///////////// Draw the radar chart blobs ////////////////
    /////////////////////////////////////////////////////////

    // The radial line function
    const radarLine = lineRadial()
      .curve(curveCatmullRom.alpha(1))
      .radius(function (d) {
        return rScale(d.value)
      })
      .angle(function (d, i) {
        return i * angleSlice
      })

    // Create a wrapper for the blobs
    const blobWrapper = g
      .selectAll('.radarWrapper')
      .data(data)
      .enter()
      .append('g')
      .attr('class', 'radarWrapper')

    // Append the backgrounds
    blobWrapper
      .append('path')
      .attr('class', 'radarArea')
      .attr('d', function (d, i) {
        return radarLine(d)
      })
      .style('fill', (d, i) => cfg.area.color[i])
      .style('fill-opacity', cfg.area.opacity)
      .on('mouseover', function (d, i) {
        // Dim all blobs
        selectAll('.radarArea')
          .transition()
          .duration(200)
          .style('fill-opacity', 0.1)
        // Bring back the hovered over blob
        select(this).transition().duration(200).style('fill-opacity', 0.7)
      })
      .on('mouseout', function () {
        // Bring back all blobs
        selectAll('.radarArea')
          .transition()
          .duration(200)
          .style('fill-opacity', cfg.opacityArea)
      })

    // Create the outlines
    blobWrapper
      .append('path')
      .attr('class', 'radarStroke')
      .attr('d', function (d, i) {
        const values = [...d]
        values.push(values[0])
        return radarLine(values)
      })
      .style('stroke-width', cfg.strokeWidth + 'px')
      .style('stroke', (d, i) => cfg.area.color[i])
      .style('fill', 'none')
      .style('filter', 'url(#glow)')

    // Append the circles
    blobWrapper
      .selectAll('.radarCircle')
      .data(function (d, i) {
        return d.map((p) => ({ ...p, serie: i }))
      })
      .enter()
      .append('circle')
      .attr('class', 'radarCircle')
      .attr('r', cfg.area.dot.radius)
      .attr('cx', function (d, i) {
        return rScale(d.value) * Math.cos(angleSlice * i - Math.PI / 2)
      })
      .attr('cy', function (d, i) {
        return rScale(d.value) * Math.sin(angleSlice * i - Math.PI / 2)
      })
      .style('fill', (d) => {
        return cfg.area.dot.color[d.serie]
      })
      .style('fill-opacity', cfg.area.dot.opacity)

    /////////////////////////////////////////////////////////
    //////// Append invisible circles for tooltip ///////////
    /////////////////////////////////////////////////////////

    // Wrapper for the invisible circles on top
    const blobCircleWrapper = g
      .selectAll('.radarCircleWrapper')
      .data(data)
      .enter()
      .append('g')
      .attr('class', 'radarCircleWrapper')

    // Append a set of invisible circles on top for the mouseover pop-up
    blobCircleWrapper
      .selectAll('.radarInvisibleCircle')
      .data(function (d, i) {
        return d
      })
      .enter()
      .append('circle')
      .attr('class', 'radarInvisibleCircle')
      .attr('r', cfg.area.dot.radius * 1.5)
      .attr('cx', function (d, i) {
        return rScale(d.value) * Math.cos(angleSlice * i - Math.PI / 2)
      })
      .attr('cy', function (d, i) {
        return rScale(d.value) * Math.sin(angleSlice * i - Math.PI / 2)
      })
      .style('fill', 'none')
      .style('pointer-events', 'all')
      .style('cursor', 'pointer')
      .on('mouseenter', (d, i, nodes) => {
        const x = rScale(d.value) * Math.cos(angleSlice * i - Math.PI / 2)
        const mousePos = mouse(nodes[i])
        const y = mousePos[1] / 2
        tooltip
          .html(cfg.popup.template(d))
          .style('left', x + 50 + 'px')
          .style('top', y + 'px')
        tooltip.classed('hidden', false)
      })
      .on('mouseout', () => {
        tooltip.classed('hidden', true)
      })

    /////////////////////////////////////////////////////////
    /////////////////// Helper Function /////////////////////
    /////////////////////////////////////////////////////////

    // Taken from http://bl.ocks.org/mbostock/7555321
    // Wraps SVG text
    function wrap(text, width) {
      text.each(function () {
        const t = select(this),
          words = t.text().split(/\s+/).reverse(),
          lineHeight = 1.4, // ems
          y = t.attr('y'),
          x = t.attr('x'),
          dy = parseFloat(t.attr('dy'))
        let word,
          line = [],
          tspan = t
            .text(null)
            .append('tspan')
            .attr('x', x)
            .attr('y', y)
            .attr('dy', dy + 'em'),
          lineNumber = 0
        while ((word = words.pop())) {
          line.push(word)
          tspan.text(line.join(' '))
          if (tspan.node().getComputedTextLength() > width) {
            line.pop()
            tspan.text(line.join(' '))
            line = [word]
            tspan = t
              .append('tspan')
              .attr('x', x)
              .attr('y', y)
              .attr('dy', ++lineNumber * lineHeight + dy + 'em')
              .text(word)
          }
        }
      })
    } // wrap
  },
}
